import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken, getToken } from "@/lib/api";
import { Loader2, LogOut, RefreshCw, Search, Inbox, ChevronRight, X, Mail, Briefcase, Newspaper } from "lucide-react";

const STATUSES = ["All", "Unread", "Read", "Archived"];
const statusColor = {
  Unread: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Read: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  Archived: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

export default function AdminMessages() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setAdmin(me.data);
        await loadAll("All");
      } catch (e) {
        setToken(null);
        navigate("/admin/login");
      }
    })();
  }, [navigate]);

  async function loadAll(statusValue) {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get("/admin/messages", { params: statusValue && statusValue !== "All" ? { status: statusValue } : {} }),
        api.get("/admin/messages/stats"),
      ]);
      setItems(list.data.items || []);
      setStats(st.data || { total: 0, by_status: {} });
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    setToken(null);
    navigate("/admin/login");
  }

  async function applyFilter(s) {
    setFilter(s);
    await loadAll(s);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => {
      return (
        (m.full_name || "").toLowerCase().includes(q) ||
        (m.company_name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q) ||
        (m.subject || "").toLowerCase().includes(q) ||
        (m.message || "").toLowerCase().includes(q)
      );
    });
  }, [items, search]);

  async function updateStatus(id, status) {
    const { data } = await api.patch(`/admin/messages/${id}`, { status });
    setItems((prev) => prev.map((p) => (p.id === id ? data : p)));
    setSelected(data);
    const st = await api.get("/admin/messages/stats");
    setStats(st.data);
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 grid grid-cols-1 lg:grid-cols-[260px_1fr]" data-testid="admin-messages-page">
      <aside className="border-r border-white/5 bg-black/40 px-5 py-6 hidden lg:flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="h-7 w-7 rounded-sm bg-gradient-to-br from-blue-600 to-blue-500 grid place-items-center">
            <span className="font-display font-bold text-white text-sm">V</span>
          </div>
          <div>
            <div className="font-display font-bold tracking-tight text-[14px]">VYR Trade</div>
            <div className="font-mono text-[9px] tracking-[0.22em] text-zinc-500">ADMIN CONSOLE</div>
          </div>
        </Link>

        <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-3">VIEWS</div>
        <div className="flex flex-col gap-1 mb-6">
          <Link
            to="/admin"
            className="text-left px-3 py-2 rounded-md text-[13px] flex items-center gap-2 text-zinc-400 hover:bg-white/5 hover:text-white border border-transparent"
            data-testid="nav-enquiries"
          >
            <Briefcase className="h-3.5 w-3.5" /> Sourcing Enquiries
          </Link>
          <Link
            to="/admin/messages"
            className="text-left px-3 py-2 rounded-md text-[13px] flex items-center gap-2 border bg-zinc-900 border-white/10 text-white"
            data-testid="nav-messages"
          >
            <Mail className="h-3.5 w-3.5" /> Contact Messages
          </Link>
          <Link
            to="/admin/insights"
            className="text-left px-3 py-2 rounded-md text-[13px] flex items-center gap-2 text-zinc-400 hover:bg-white/5 hover:text-white border border-transparent"
            data-testid="nav-insights"
          >
            <Newspaper className="h-3.5 w-3.5" /> Insights
          </Link>
        </div>

        <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-3">FILTERS</div>
        <div className="flex flex-col gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => applyFilter(s)}
              className={`text-left px-3 py-2 rounded-md text-[13px] flex items-center justify-between border transition-colors ${
                filter === s ? "bg-zinc-900 border-white/10 text-white" : "border-transparent text-zinc-400 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`msg-filter-${s.toLowerCase()}`}
            >
              <span>{s}</span>
              <span className="font-mono text-[11px] text-zinc-500 tnum">
                {s === "All" ? stats.total : stats.by_status?.[s] || 0}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-auto pt-6 border-t border-white/5">
          <div className="text-[12px] text-zinc-400 break-all">{admin?.email}</div>
          <button onClick={logout} className="mt-3 inline-flex items-center gap-2 text-[12px] text-zinc-400 hover:text-white" data-testid="admin-logout">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <section className="min-w-0">
        <div className="border-b border-white/5 bg-zinc-900/40 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-amber-400">CONTACT MESSAGES</div>
            <div className="font-display text-2xl font-semibold mt-1">{stats.total} <span className="text-zinc-500 text-base font-normal">total</span></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, subject…"
                className="pl-9 pr-3 py-2 bg-zinc-950 border border-white/10 rounded-md text-[13px] w-72 focus:border-amber-400/60 focus:outline-none"
                data-testid="messages-search"
              />
            </div>
            <button onClick={() => loadAll(filter)} className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/10 rounded-md text-[13px] hover:bg-white/5" data-testid="messages-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/5 border-b border-white/5">
          {STATUSES.slice(1).map((s) => (
            <div key={s} className="bg-zinc-950 p-4">
              <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500">{s.toUpperCase()}</div>
              <div className="mt-1 font-display text-2xl font-bold tnum">{stats.by_status?.[s] || 0}</div>
            </div>
          ))}
        </div>

        <div className="p-6">
          <div className="border border-white/5 rounded-lg overflow-hidden bg-zinc-900/40">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-zinc-950/60 text-zinc-500 font-mono text-[11px] tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">ID</th>
                    <th className="text-left px-4 py-3">DATE</th>
                    <th className="text-left px-4 py-3">FROM</th>
                    <th className="text-left px-4 py-3">SUBJECT</th>
                    <th className="text-left px-4 py-3">STATUS</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500" data-testid="messages-empty"><Inbox className="h-4 w-4 inline mr-2" /> No messages yet.</td></tr>
                  ) : (
                    filtered.map((m) => (
                      <tr key={m.id} className="border-t border-white/5 hover:bg-zinc-900 cursor-pointer" onClick={() => setSelected(m)} data-testid={`msg-row-${m.id}`}>
                        <td className="px-4 py-3 font-mono text-zinc-400">{m.id.slice(0, 8).toUpperCase()}</td>
                        <td className="px-4 py-3 text-zinc-400 font-mono">{new Date(m.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-zinc-100">
                          <div>{m.full_name}</div>
                          <div className="text-zinc-500 text-[12px]">{m.email}{m.company_name ? ` · ${m.company_name}` : ""}</div>
                        </td>
                        <td className="px-4 py-3 text-zinc-300 max-w-[280px] truncate">{m.subject || <span className="text-zinc-500">—</span>}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 rounded-md text-[11px] font-mono border ${statusColor[m.status] || ""}`}>
                            {m.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-zinc-500"><ChevronRight className="h-4 w-4 inline" /></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {selected && (
        <MessageDrawer message={selected} onClose={() => setSelected(null)} onUpdate={updateStatus} />
      )}
    </div>
  );
}

function MessageDrawer({ message, onClose, onUpdate }) {
  const [status, setStatus] = useState(message.status);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setStatus(message.status); }, [message.id]);

  async function save() {
    setSaving(true);
    try { await onUpdate(message.id, status); } finally { setSaving(false); }
  }

  const mailto = `mailto:${message.email}?subject=${encodeURIComponent("Re: " + (message.subject || "Your message to VYR Trade"))}`;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="msg-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl h-full bg-zinc-950 border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 bg-zinc-950/95 backdrop-blur-md border-b border-white/5 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-amber-400">MESSAGE · {message.id.slice(0, 8).toUpperCase()}</div>
            <div className="mt-1 font-display text-lg font-semibold">{message.full_name}</div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white" data-testid="msg-drawer-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <KV k="Email" v={<a href={mailto} className="text-amber-400 hover:underline">{message.email}</a>} />
          {message.company_name && <KV k="Company" v={message.company_name} />}
          {message.subject && <KV k="Subject" v={message.subject} />}
          <KV k="Received" v={new Date(message.created_at).toLocaleString()} mono />
          <div>
            <div className="text-[11px] font-mono tracking-[0.18em] text-zinc-500">MESSAGE</div>
            <div className="mt-1 text-[14px] text-zinc-200 whitespace-pre-wrap border border-white/5 bg-zinc-900/40 rounded-md p-4">{message.message}</div>
          </div>

          <div className="pt-4 border-t border-white/5">
            <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 mb-2">UPDATE</div>
            <label className="block text-[12px] text-zinc-400 mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-zinc-950 border border-white/10 rounded-md px-3 py-2 text-[14px] focus:border-amber-400/60 focus:outline-none" data-testid="msg-drawer-status">
              {STATUSES.slice(1).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="mt-3 flex gap-2">
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-60" data-testid="msg-drawer-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? "Saving…" : "Save status"}
              </button>
              <a href={mailto} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-amber-400/30 text-amber-300 hover:bg-amber-400/10 text-sm font-medium" data-testid="msg-drawer-reply">
                <Mail className="h-4 w-4" /> Reply via email
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-start">
      <div className="text-[11px] font-mono tracking-[0.18em] text-zinc-500">{k.toUpperCase()}</div>
      <div className={`col-span-2 text-[14px] text-zinc-200 ${mono ? "font-mono" : ""}`}>{v}</div>
    </div>
  );
}
